import { forwardRef, useState, type InputHTMLAttributes } from 'react';

import { cn } from '../lib/cn';

export interface PasswordInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  /** Accessible label for the toggle button while the password is hidden (announces the "reveal" action). */
  showLabel: string;
  /** Accessible label for the toggle button while the password is visible (announces the "hide" action). */
  hideLabel: string;
  /** Optional className applied to the wrapping container element (not the input itself). */
  containerClassName?: string;
}

export const PasswordInput = forwardRef<HTMLInputElement, PasswordInputProps>(function PasswordInput(
  { className, containerClassName, hideLabel, showLabel, ...props },
  ref,
) {
  const [visible, setVisible] = useState(false);

  return (
    <div className={cn('relative flex items-center', containerClassName)}>
      <input
        className={cn(
          'flex h-9 w-full rounded-[var(--radius-control)] border border-[var(--border)] bg-[var(--background)] px-3 py-2 pr-9 text-sm text-[var(--foreground)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--secondary)] disabled:cursor-not-allowed disabled:opacity-50',
          className,
        )}
        ref={ref}
        type={visible ? 'text' : 'password'}
        {...props}
      />
      <button
        aria-label={visible ? hideLabel : showLabel}
        aria-pressed={visible}
        className="absolute right-1.5 inline-flex size-6 items-center justify-center rounded-[var(--radius-control)] text-[var(--foreground-muted)] transition-colors hover:bg-[var(--background-muted)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--secondary)]"
        data-testid="password-input-toggle"
        onClick={() => setVisible((current) => !current)}
        type="button"
      >
        <span aria-hidden="true" className="material-symbols-outlined text-[18px]">
          {visible ? 'visibility_off' : 'visibility'}
        </span>
      </button>
    </div>
  );
});

PasswordInput.displayName = 'PasswordInput';
